import { canAccessInternalPath, hasPermission } from "@/lib/roles";
import type { SearchHit } from "@/types/internal-search";

type RoleValue = string | null | undefined;
type UserRouteParams = {
  email?: string;
  userId?: string;
};

function encode(value: string): string {
  return encodeURIComponent(value);
}

function canAccess(role: RoleValue, pathname: string): boolean {
  return canAccessInternalPath(role, pathname);
}

export function resolveInternalUsersDestination(
  userRole: RoleValue,
  route: UserRouteParams = {},
): string | null {
  if (!hasPermission(userRole, "users:view")) {
    return null;
  }

  if (canAccess(userRole, "/admin")) {
    if (route.email) {
      return `/admin?tab=users&email=${encode(route.email)}`;
    }
    return route.userId
      ? `/admin?tab=users&userId=${encode(route.userId)}`
      : "/admin?tab=users";
  }
  if (canAccess(userRole, "/logistics/users")) {
    return route.userId
      ? `/logistics/users?userId=${encode(route.userId)}`
      : "/logistics/users";
  }
  if (canAccess(userRole, "/sales/crm/people")) {
    return route.email
      ? `/sales/crm/people?email=${encode(route.email)}`
      : "/sales/crm/people";
  }
  return null;
}

export function resolveInternalSearchDestination(
  hit: SearchHit,
  userRole: RoleValue,
): string | null {
  switch (hit.entity) {
    case "user": {
      return resolveInternalUsersDestination(userRole, hit.route);
    }

    case "order": {
      if (!hit.route.orderNumber) return null;
      if (canAccess(userRole, "/admin")) {
        return `/admin?tab=orders&orderNumber=${encode(hit.route.orderNumber)}`;
      }
      if (canAccess(userRole, "/logistics")) {
        return `/logistics/orders?orderNumber=${encode(hit.route.orderNumber)}`;
      }
      return null;
    }

    case "contract": {
		if (canAccess(userRole, "/admin") && hit.route.contractNumber) {
			return `/admin?tab=contracts&contractNumber=${encode(hit.route.contractNumber)}`;
		}
		if (canAccess(userRole, "/sales/contracts") && hit.route.contractId) {
			return `/sales/contracts/${encode(hit.route.contractId)}`;
		}
		return null;
	}

    case "cargo": {
      const cargoLookup = hit.route.ociNumber ?? hit.route.cargoUuid;
      if (!cargoLookup) return null;

      if (canAccess(userRole, "/admin")) {
        return `/admin?tab=inventory&oci=${encode(cargoLookup)}`;
      }
      if (canAccess(userRole, "/inventory")) {
        return `/inventory/cargos?ociNumber=${encode(cargoLookup)}`;
      }
      return null;
    }

    case "product": {
      if (!hit.route.productName) return null;

      if (canAccess(userRole, "/admin")) {
        return `/admin?tab=inventory&productName=${encode(hit.route.productName)}`;
      }
      if (canAccess(userRole, "/inventory")) {
        return `/inventory/products?name=${encode(hit.route.productName)}`;
      }
      return null;
    }

    case "thread": {
      if (!hit.route.threadId) return null;
      if (canAccess(userRole, "/support")) {
        return `/support?threadId=${encode(hit.route.threadId)}`;
      }
      return null;
    }

    default:
      return null;
  }
}
